/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import imageTitle from 'assets/img/illustrations/reports-greeting.png';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import SkeletonTable from 'components/common/SkeletonTable';
import SoftBadge from 'components/common/SoftBadge';
import IconItem from 'components/common/icon/IconItem';
import FooterPaginacion from 'akasia-components/administracion/usuarios-sistema/FooterPaginacion';
import TitleSelectorSupportDesk from 'akasia-components/dashboards/general/TitleSelectorSupportDesk';
import ModalHistorialPlanFacturacionMx from 'akasia-components/cliente/components/ModalHistorialPlanFacturacionMx';

const RESUMEN_INICIAL = {
    iTotalTimbresUsados: 0,
    iTotalTimbresDisponibles: 0,
    dTotalIngresoMXN: 0,
    dPctInventarioDisponible: 0,
    iTimbresUsadosMesActual: 0,
    iTimbresUsadosMesAnterior: 0,
    iTimbresDisponiblesMesActual: 0,
    iTimbresDisponiblesMesAnterior: 0,
    iTotalPlanesGlobal: 0
};

const parseReporteDetalles = (detalles) => {
    if (!detalles || typeof detalles !== 'object') {
        return { planes: [], totalRecords: 0, resumen: RESUMEN_INICIAL };
    }

    const tables = Object.values(detalles).filter(Array.isArray);
    const planes = tables.find((rows) => rows.some((r) => r?.idPlanFacturaMx != null)) || [];
    const resumen = tables.flat().find((r) => r?.iTotalTimbresUsados != null) || RESUMEN_INICIAL;
    const totalRow = tables.flat().find((r) => r?.totalRecords != null);

    return {
        planes,
        totalRecords: totalRow?.totalRecords || 0,
        resumen
    };
};

const formatNumero = (value) => Number(value || 0).toLocaleString('es-MX');

const formatMxn = (value) =>
    Number(value || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

const formatFechaCorta = (fechaStr) => {
    if (!fechaStr) return null;
    const fecha = new Date(fechaStr);
    if (Number.isNaN(fecha.getTime())) return null;
    return fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getEtiquetaRelativa = (fechaStr) => {
    if (!fechaStr) return null;

    const fecha = new Date(fechaStr);
    if (Number.isNaN(fecha.getTime())) return null;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const diaFactura = new Date(fecha);
    diaFactura.setHours(0, 0, 0, 0);

    const diffDays = Math.round((hoy - diaFactura) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'HOY';
    if (diffDays === 1) return 'AYER';
    return `HACE ${diffDays} DÍAS`;
};

const getSaldoTimbres = (disponibles) => {
    const disp = Number(disponibles || 0);

    if (disp === 0) {
        return { label: 'SIN SALDO', bg: 'secondary' };
    }
    if (disp <= 25) {
        return { label: 'CRÍTICO', bg: 'danger' };
    }
    if (disp <= 200) {
        return { label: 'BAJO', bg: 'warning' };
    }
    return { label: 'NORMAL', bg: 'success' };
};

const calcVariacionMes = (actual, anterior) => {
    const a = Number(actual || 0);
    const b = Number(anterior || 0);
    if (b === 0) return a > 0 ? 100 : 0;
    return ((a - b) / b) * 100;
};

const formatMesAnio = (fecha) =>
    fecha
        .toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })
        .replaceAll('.', '')
        .trim()
        .toLowerCase();

const formatMesAnioTitulo = (fecha) => {
    const etiqueta = fecha.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    return etiqueta.charAt(0).toUpperCase() + etiqueta.slice(1);
};

const getMesesReferencia = () => {
    const mesActual = new Date();
    const mesAnterior = new Date(mesActual.getFullYear(), mesActual.getMonth() - 1, 1);
    return { mesActual, mesAnterior };
};

const formatDiferenciaMes = (actual, anterior, etiquetaMesAnterior) => {
    const diff = Number(actual || 0) - Number(anterior || 0);
    if (diff === 0) return `Igual que en ${etiquetaMesAnterior}`;
    if (diff > 0) return `${formatNumero(diff)} más que en ${etiquetaMesAnterior}`;
    return `${formatNumero(Math.abs(diff))} menos que en ${etiquetaMesAnterior}`;
};

const getEtiquetaComparacionMeses = () => {
    const { mesActual, mesAnterior } = getMesesReferencia();
    return `${formatMesAnio(mesActual)} vs ${formatMesAnio(mesAnterior)}`;
};

const formatMesCorto = (fecha) =>
    fecha.toLocaleDateString('es-MX', { month: 'short' }).replaceAll('.', '').trim();

const MiniComparacionMesesChart = ({ valorAnterior, valorActual, mesAnterior, mesActual }) => {
    const anterior = Number(valorAnterior || 0);
    const actual = Number(valorActual || 0);
    const max = Math.max(anterior, actual, 1);

    const width = 96;
    const height = 72;
    const padX = 14;
    const padTop = 16;
    const padBottom = 18;
    const chartH = height - padTop - padBottom;
    const x1 = padX;
    const x2 = width - padX;
    const yBase = padTop + chartH;
    const y1 = yBase - (anterior / max) * chartH;
    const y2 = yBase - (actual / max) * chartH;

    const trendColor = actual > anterior ? '#00d27a' : actual < anterior ? '#e63757' : '#748194';

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className="flex-shrink-0">
            <line x1={x1} y1={yBase} x2={x2} y2={yBase} stroke="#d8e2ef" strokeWidth="1" />
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={trendColor} strokeWidth="2" strokeLinecap="round" />
            <circle cx={x1} cy={y1} r="4.5" fill="#748194" />
            <circle cx={x2} cy={y2} r="4.5" fill={trendColor} />
            <text x={x1} y={y1 - 7} textAnchor="middle" fontSize="9" fontWeight="600" fill="#344050">
                {formatNumero(anterior)}
            </text>
            <text x={x2} y={y2 - 7} textAnchor="middle" fontSize="9" fontWeight="600" fill={trendColor}>
                {formatNumero(actual)}
            </text>
            <text x={x1} y={height - 4} textAnchor="middle" fontSize="8" fill="#748194">
                {formatMesCorto(mesAnterior)}
            </text>
            <text x={x2} y={height - 4} textAnchor="middle" fontSize="8" fill="#748194">
                {formatMesCorto(mesActual)}
            </text>
        </svg>
    );
};

const KpiDetalleFila = ({ label, value, destacado = false }) => (
    <div className="d-flex justify-content-between align-items-baseline gap-2 py-1 border-bottom border-200">
        <span className="text-600 fs--1">{label}</span>
        <span className={`${destacado ? 'fw-bold text-dark' : 'fw-semi-bold'} fs--1`}>{value}</span>
    </div>
);

const KpiTimbresUsadosCard = ({ resumen, detalle, onToggle }) => {
    const { mesActual, mesAnterior } = getMesesReferencia();
    const etiquetaMesActual = formatMesAnioTitulo(mesActual);
    const etiquetaMesAnterior = formatMesAnioTitulo(mesAnterior);

    return (
        <Card
            className="h-100 shadow-none border"
            role="button"
            onClick={onToggle}
            style={{ cursor: 'pointer' }}
            title={detalle ? 'Ver resumen del mes en curso' : 'Ver detalle de uso'}
        >
            <Card.Body className="py-3">
                <div className="d-flex align-items-center mb-2">
                    <IconItem
                        tag="div"
                        icon="ticket-alt"
                        bg="soft-primary"
                        color="primary"
                        size="sm"
                        iconClass="fs-0"
                        className="me-2 shadow-none"
                    />
                    <span className="text-uppercase fs--2 fw-semi-bold text-600">
                        {detalle ? 'Detalle timbres usados' : 'Timbres usados'}
                    </span>
                </div>

                {!detalle ? (
                    <>
                        <div className="d-flex justify-content-between align-items-center gap-2">
                            <div className="flex-grow-1 min-w-0">
                                <p className="fs-3 fw-bold mb-1 text-dark">
                                    {formatNumero(resumen.iTimbresUsadosMesActual)}
                                </p>
                                <p className="fs--1 mb-0 text-dark">{etiquetaMesActual}</p>
                                <p className="fs--2 mb-0 text-muted mt-1">
                                    {formatMesAnioTitulo(mesAnterior)}: {formatNumero(resumen.iTimbresUsadosMesAnterior)} timbres
                                </p>
                            </div>
                            <MiniComparacionMesesChart
                                valorAnterior={resumen.iTimbresUsadosMesAnterior}
                                valorActual={resumen.iTimbresUsadosMesActual}
                                mesAnterior={mesAnterior}
                                mesActual={mesActual}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <KpiDetalleFila
                            label={etiquetaMesActual}
                            value={`${formatNumero(resumen.iTimbresUsadosMesActual)} timbres`}
                            destacado
                        />
                        <KpiDetalleFila
                            label={etiquetaMesAnterior}
                            value={`${formatNumero(resumen.iTimbresUsadosMesAnterior)} timbres`}
                        />
                        <KpiDetalleFila
                            label="Total histórico"
                            value={formatNumero(resumen.iTotalTimbresUsados)}
                        />
                        <p className="fs--2 mb-0 text-muted mt-2">
                            {formatDiferenciaMes(
                                resumen.iTimbresUsadosMesActual,
                                resumen.iTimbresUsadosMesAnterior,
                                etiquetaMesAnterior
                            )}
                        </p>
                    </>
                )}

                <p className="fs--2 mb-0 text-primary mt-2">
                    {detalle ? 'Clic para ver el mes en curso' : 'Clic para ver detalle'}
                </p>
            </Card.Body>
        </Card>
    );
};

const KpiCard = ({ icon, color, title, value, subtitle, subtitleLine2, subtitleClass = 'text-muted' }) => (
    <Card className="h-100 shadow-none border">
        <Card.Body className="py-3">
            <div className="d-flex align-items-center mb-2">
                <IconItem
                    tag="div"
                    icon={icon}
                    bg={`soft-${color}`}
                    color={color}
                    size="sm"
                    iconClass="fs-0"
                    className="me-2 shadow-none"
                />
                <span className="text-uppercase fs--2 fw-semi-bold text-600">{title}</span>
            </div>
            <p className="fs-3 fw-bold mb-1 text-dark">{value}</p>
            {subtitle && <p className={`fs--1 mb-0 ${subtitleClass}`}>{subtitle}</p>}
            {subtitleLine2 && <p className="fs--2 mb-0 text-muted">{subtitleLine2}</p>}
        </Card.Body>
    </Card>
);

const ReportePlanesFacturacionMx = () => {
    const { token, setToken, idCatTipoUsuario } = useToken();
    const navigate = useNavigate();
    const { showErrorToast } = useToast();

    const [planes, setPlanes] = useState([]);
    const [resumen, setResumen] = useState(RESUMEN_INICIAL);
    const [cargando, setCargando] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showModalHistorial, setShowModalHistorial] = useState(false);
    const [planHistorial, setPlanHistorial] = useState(null);
    const [idClienteHistorial, setIdClienteHistorial] = useState(null);
    const [kpiUsadosDetalle, setKpiUsadosDetalle] = useState(false);

    const tokenRef = useRef(token);
    tokenRef.current = token;

    useEffect(() => {
        if (idCatTipoUsuario != 1) {
            navigate('/');
        }
    }, [idCatTipoUsuario, navigate]);

    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(searchText.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        setPageNumber(1);
    }, [searchDebounced, pageSize]);

    useEffect(() => {
        if (idCatTipoUsuario != 1) return;

        let cancelled = false;

        const cargarReporte = async () => {
            setCargando(true);

            try {
                const response = await fetch('/api/Administradores/ReportePlanesFacturacionMx', {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + tokenRef.current,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        searchText: searchDebounced || null,
                        pageNumber,
                        pageSize
                    })
                });

                if (cancelled) return;

                if (response.status === 401) {
                    setToken(null);
                    navigate('/');
                    return;
                }

                if (!response.ok) {
                    showErrorToast('No se pudo cargar el reporte de planes de facturación.');
                    return;
                }

                const data = await response.json();
                if (data.iResponseType !== 1) {
                    showErrorToast(data.vMessage || 'Error al obtener el reporte.');
                    setPlanes([]);
                    setTotalRecords(0);
                    setResumen(RESUMEN_INICIAL);
                    return;
                }

                const detalles = JSON.parse(data.detalles);
                const parsed = parseReporteDetalles(detalles);
                setPlanes(parsed.planes);
                setTotalRecords(parsed.totalRecords);
                setResumen(parsed.resumen);
            } catch (error) {
                if (!cancelled) {
                    showErrorToast('Error: ' + error.message);
                }
            } finally {
                if (!cancelled) {
                    setCargando(false);
                }
            }
        };

        cargarReporte();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line
    }, [idCatTipoUsuario, pageNumber, pageSize, searchDebounced, token]);

    const etiquetaComparacionMeses = getEtiquetaComparacionMeses();
    const variacionDisponibles = calcVariacionMes(
        resumen.iTimbresDisponiblesMesActual,
        resumen.iTimbresDisponiblesMesAnterior
    );

    const handleAbrirHistorial = useCallback((row) => {
        setPlanHistorial({
            idPlanFacturaMx: row.idPlanFacturaMx,
            vRfc: row.vRfc,
            iCantidades: row.iTimbresDisponibles
        });
        setIdClienteHistorial(row.idClienteAkasia);
        setShowModalHistorial(true);
    }, []);

    const handleCerrarHistorial = useCallback(() => {
        setShowModalHistorial(false);
        setPlanHistorial(null);
        setIdClienteHistorial(null);
    }, []);

    const columns = useMemo(() => [
        {
            accessor: 'vClienteAkasia',
            Header: 'Cliente Akasia',
            headerClassName: 'text-start bg-light text-uppercase fs--2',
            className: 'text-start',
            Cell: ({ row }) => {
                const { idClienteAkasia, vClienteAkasia } = row.original;
                return (
                    <div>
                        <span className="text-dark">{vClienteAkasia || '—'}</span>
                        <div className="text-muted fs--2">ID: {idClienteAkasia}</div>
                    </div>
                );
            }
        },
        {
            accessor: 'vRfc',
            Header: 'RFC Registrado',
            headerClassName: 'text-start bg-light text-uppercase fs--2',
            className: 'text-start font-monospace',
            Cell: ({ row }) => {
                const { vRfc } = row.original;
                return (
                    <button
                        type="button"
                        className="btn btn-link p-0 fw-bold text-dark text-decoration-none border-0 font-monospace"
                        onClick={() => handleAbrirHistorial(row.original)}
                        title="Ver historial del plan"
                    >
                        {vRfc || '—'}
                    </button>
                );
            }
        },
        {
            accessor: 'iTimbresAdquiridos',
            Header: 'Timbres Adquiridos',
            headerClassName: 'text-center bg-light text-uppercase fs--2',
            className: 'text-center fw-semi-bold',
            Cell: ({ value }) => formatNumero(value)
        },
        {
            accessor: 'iTimbresDisponibles',
            Header: 'Timbres Disponibles',
            headerClassName: 'text-center bg-light text-uppercase fs--2',
            className: 'text-center',
            Cell: ({ row }) => {
                const { iTimbresDisponibles } = row.original;
                const saldo = getSaldoTimbres(iTimbresDisponibles);
                return (
                    <div className="d-flex flex-column align-items-center gap-1">
                        <span className="fw-bold">{formatNumero(iTimbresDisponibles)}</span>
                        <SoftBadge bg={saldo.bg} className="fs--2">{saldo.label}</SoftBadge>
                    </div>
                );
            }
        },
        {
            accessor: 'dTotalDineroGastadoMXN',
            Header: 'Total Dinero Gastado',
            headerClassName: 'text-center bg-light text-uppercase fs--2',
            className: 'text-center fw-semi-bold',
            Cell: ({ value }) => formatMxn(value)
        },
        {
            accessor: 'dFechaUltimaFactura',
            Header: 'Última Factura',
            headerClassName: 'text-start bg-light text-uppercase fs--2',
            className: 'text-start',
            Cell: ({ value }) => {
                const etiqueta = getEtiquetaRelativa(value);
                const fecha = formatFechaCorta(value);

                if (!etiqueta) {
                    return <span className="text-muted">Sin facturas</span>;
                }

                return (
                    <div className="d-flex flex-column align-items-start gap-1">
                        <SoftBadge bg={etiqueta === 'HOY' ? 'info' : 'secondary'} className="fs--2">
                            {etiqueta}
                        </SoftBadge>
                        {fecha && <span className="text-muted fs--2">{fecha}</span>}
                    </div>
                );
            }
        }
    ], [handleAbrirHistorial]);

    return (
        <>
            <Row className="g-3">
                <Col xxl={12}>
                    <Row className="g-0 h-100">
                        <Col xs={12}>
                            <TitleSelectorSupportDesk
                                title1="Akasia"
                                title2="Reporte Planes Facturación MX"
                                barChart={imageTitle}
                                barChartWidth={72}
                                barChartClassName="ms-n2"
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            

            <Row className="g-3 mb-2">
                <Col md={4}>
                    <KpiTimbresUsadosCard
                        resumen={resumen}
                        detalle={kpiUsadosDetalle}
                        onToggle={() => setKpiUsadosDetalle((prev) => !prev)}
                    />
                </Col>
                <Col md={4}>
                    <KpiCard
                        icon="archive"
                        color="info"
                        title="Timbres Disponibles Global"
                        value={formatNumero(resumen.iTotalTimbresDisponibles)}
                        subtitle={`${variacionDisponibles >= 0 ? '+' : ''}${variacionDisponibles.toFixed(1)}%`}
                        subtitleLine2={etiquetaComparacionMeses}
                        subtitleClass={variacionDisponibles >= 0 ? 'text-success' : 'text-danger'}
                    />
                </Col>
                <Col md={4}>
                    <KpiCard
                        icon="file-invoice-dollar"
                        color="success"
                        title="Ingreso Total Generado"
                        value={formatMxn(resumen.dTotalIngresoMXN)}
                        subtitle="MXN · pagos en planes de facturación"
                    />
                </Col>
            </Row>

            <InputGroup className="mb-2">
                <InputGroup.Text className="bg-white">
                    <FontAwesomeIcon icon="search" className="text-400" />
                </InputGroup.Text>
                <Form.Control
                    type="search"
                    className="border-start-0"
                    placeholder="Buscar por RFC o Nombre del Cliente..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    aria-label="Buscar plan de facturación"
                />
            </InputGroup>

            <Card className="shadow-none border">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                    <Card.Title className="mb-0 fs-1">Listado de Facturación</Card.Title>
                    {totalRecords > 0 && (
                        <span className="text-muted fs--1">
                            {((pageNumber - 1) * pageSize) + 1}–{Math.min(pageNumber * pageSize, totalRecords)} de {totalRecords}
                        </span>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {cargando ? (
                        <div className="p-3">
                            <SkeletonTable columnNames={columns.map(c => c.Header)} rows={pageSize} />
                        </div>
                    ) : (
                        <AdvanceTableWrapper columns={columns} data={planes} sortable pagination={false}>
                            <AdvanceTable
                                table
                                headerClassName="bg-200 text-900 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    size: 'sm',
                                    responsive: true,
                                    className: 'fs--1 mb-0'
                                }}
                            />
                        </AdvanceTableWrapper>
                    )}

                    {!cargando && planes.length === 0 && (
                        <p className="text-center text-muted py-4 mb-0">No se encontraron planes de facturación.</p>
                    )}
                </Card.Body>
                <Card.Footer className="bg-light border-top">
                    <Row className="align-items-center g-2">
                        <FooterPaginacion
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            pageNumber={pageNumber}
                            onPageSizeChange={(e) => setPageSize(Number(e.target.value))}
                            onNextPage={() => setPageNumber(pageNumber + 1)}
                            onPreviousPage={() => setPageNumber(pageNumber - 1)}
                            onGotoPage={(page) => setPageNumber(page + 1)}
                        />
                    </Row>
                </Card.Footer>
            </Card>

            <ModalHistorialPlanFacturacionMx
                show={showModalHistorial}
                onHide={handleCerrarHistorial}
                idClienteAkasia={idClienteHistorial}
                plan={planHistorial}
            />
        </>
    );
};

export default ReportePlanesFacturacionMx;
